/**
 * Superpowers Card Component
 * Displays up to 3 student strengths with check icons.
 * Shows friendly placeholder when no strengths data available.
 *
 * Redesign (2026-06): restyled as a green "superpowers" card to align with the
 * dashboard redesign. The empty-state copy and strength text are preserved verbatim
 * so the StudentDashboardPage tests remain valid.
 */

import { Check } from 'lucide-react';

export interface SuperpowersCardProps {
  /** Array of strength descriptions from latest session report */
  strengths: string[];
}

export function SuperpowersCard({ strengths }: SuperpowersCardProps) {
  const hasStrengths = strengths.length > 0;

  return (
    <div className="flex h-full flex-col rounded-2xl border border-student-200 bg-student-50 p-5 shadow-sm">
      <h3 className="mb-3 flex items-center gap-2 font-display text-base font-semibold text-student-700">
        <span aria-hidden="true">💪</span> Superpowers
      </h3>
      {hasStrengths ? (
        <div className="space-y-2">
          {strengths.slice(0, 3).map((strength, index) => (
            <div key={index} className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-student-600" aria-hidden="true" />
              <span className="text-sm text-otter-earth-700">{strength}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="py-2 text-sm text-gray-500">
          Your superpowers will appear after a few sessions!
        </p>
      )}
    </div>
  );
}
